import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Search,
  Save,
  X,
  Trash2,
  RefreshCw,
  Download,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomersTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const [customers, setCustomers] = useState([]);
  const [originalCustomers, setOriginalCustomers] = useState([]);
  const [modifiedIds, setModifiedIds] = useState(new Set());
  
  const [paymentTerms, setPaymentTerms] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers`, {
        params: { sort: sortField, order: sortOrder }
      });
      setCustomers(response.data.customers);
      setOriginalCustomers(JSON.parse(JSON.stringify(response.data.customers)));
      setPaymentTerms(response.data.payment_terms);
      setModifiedIds(new Set());
    } catch (err) {
      console.error("Error fetching customers:", err);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [sortField, sortOrder]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Filter customers by search
  const filteredCustomers = useMemo(() => {
    if (!searchQuery) return customers;
    const query = searchQuery.toLowerCase();
    return customers.filter(c => 
      c.name.toLowerCase().includes(query) ||
      (c.payment_term && c.payment_term.toLowerCase().includes(query))
    );
  }, [customers, searchQuery]);

  // Check for changes
  const hasChanges = modifiedIds.size > 0;

  // Handle field change
  const handleFieldChange = (customerId, field, value) => {
    setCustomers(prev => prev.map(c => {
      if (c.id === customerId) {
        return { ...c, [field]: value };
      }
      return c;
    }));
    setModifiedIds(prev => new Set([...prev, customerId]));
  };

  // Sync from invoices
  const handleSyncFromInvoices = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/customers/bulk-from-invoices`);
      toast.success(`Synced: ${response.data.created} new, ${response.data.skipped} already existed`);
      await fetchCustomers();
    } catch (err) {
      console.error("Error syncing customers:", err);
      toast.error("Failed to sync customers");
    } finally {
      setSyncing(false);
    }
  };

  // Save changes
  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      const updates = customers
        .filter(c => modifiedIds.has(c.id))
        .map(c => ({
          id: c.id,
          payment_term: c.payment_term
        }));
      
      await axios.patch(`${API}/customers/bulk`, { updates });
      
      setOriginalCustomers(JSON.parse(JSON.stringify(customers)));
      setModifiedIds(new Set());
      toast.success("Customers saved successfully");
    } catch (err) {
      console.error("Error saving customers:", err);
      toast.error("Failed to save customers");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setCustomers(JSON.parse(JSON.stringify(originalCustomers)));
    setModifiedIds(new Set());
    toast.info("Changes discarded");
  };

  // Delete customer
  const handleDelete = async (customer) => {
    try {
      const response = await axios.delete(`${API}/customers/${customer.id}`);
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      toast.success("Customer deleted");
      setCustomers(prev => prev.filter(c => c.id !== customer.id));
      setOriginalCustomers(prev => prev.filter(c => c.id !== customer.id));
      setModifiedIds(prev => {
        const next = new Set(prev);
        next.delete(customer.id);
        return next;
      });
    } catch (err) {
      console.error("Error deleting customer:", err);
      toast.error("Failed to delete customer");
    }
    setDeleteConfirm(null);
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = ["ID", "Name", "Payment Term"];
    const rows = customers.map(c => [
      c.id,
      `"${c.name}"`,
      c.payment_term || ""
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  // Toggle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-state">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="customers-tab">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncFromInvoices}
            disabled={syncing}
            data-testid="sync-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync from Invoices
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={customers.length === 0}
            data-testid="export-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Customers Table */}
      {customers.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center" data-testid="empty-state">
          <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No customers</h3>
          <p className="text-muted-foreground mb-4">
            Import sales invoices to automatically add customers, 
            or click "Sync from Invoices" to synchronize existing ones.
          </p>
        </div>
      ) : (
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-muted/30 border-b">
                <tr>
                  <th 
                    className="text-left px-4 py-3 font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort("name")}
                  >
                    Customer {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th 
                    className="text-left px-4 py-3 font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 w-[200px]"
                    onClick={() => handleSort("payment_term")}
                  >
                    Payment Term {sortField === "payment_term" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer, index) => {
                  const isModified = modifiedIds.has(customer.id);
                  
                  return (
                    <tr 
                      key={customer.id}
                      className={`border-b last:border-b-0 transition-colors hover:bg-muted/20 ${index % 2 === 0 ? 'bg-white' : 'bg-muted/5'}`}
                      data-testid={`customer-row-${customer.id}`}
                    >
                      <td className="px-4 py-2">
                        <span className="font-medium">{customer.name}</span>
                        {isModified && (
                          <span className="ml-2 inline-block w-2 h-2 rounded-full bg-amber-500" title="Unsaved changes" />
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          value={customer.payment_term || ""}
                          onValueChange={(val) => handleFieldChange(customer.id, "payment_term", val)}
                        >
                          <SelectTrigger 
                            className={`h-8 text-sm ${!customer.payment_term ? 'text-muted-foreground italic' : ''} ${isModified ? 'border-amber-400' : ''}`}
                            data-testid={`payment-term-${customer.id}`}
                          >
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {paymentTerms.map(term => (
                              <SelectItem key={term} value={term}>
                                {term}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-2 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirm(customer)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-${customer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Table Footer */}
          <div className="px-4 py-3 bg-muted/20 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {filteredCustomers.length} of {customers.length} customers
              {modifiedIds.size > 0 && ` • ${modifiedIds.size} modified`}
            </span>
          </div>
        </div>
      )}

      {/* Floating Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            {modifiedIds.size} changes
          </span>
          <Button variant="outline" onClick={handleDiscard} className="rounded-full" data-testid="discard-button">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.name}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomersTab;
